import React from 'react';
import { Table, headerTitle } from './Table';
import { Spinner } from './Feedback';

// DataTable — the shared, responsive data table for the whole app. One column config
// drives BOTH the desktop table (via the standardized <Table> primitive) AND an
// automatic stacked-card layout on phones (< md). Adopt this instead of hand-writing a
// <table> + a separate mobile card list per module — new tables get consistent styling
// and the responsive card view for free. See docs/design-system.md §7.
//
// Mobile card anatomy (from each column's `mobile` placement):
//   title    → card heading (bold; the clickable/primary field)
//   subtitle → secondary line under the title
//   trailing → top-right of the card (status badge, total, actions menu)
//   meta     → labelled cell in the 2-col grid (default when `mobile` is unset)
//   hidden   → omitted on mobile

export type DataTableMobileSlot = 'title' | 'subtitle' | 'trailing' | 'meta' | 'hidden';

export interface DataTableColumn<T> {
  /** Stable key; also the sort key sent to `onSort` when `sortable`. */
  key: string;
  header: React.ReactNode;
  /** Cell content for a row. */
  cell: (row: T) => React.ReactNode;
  /** Drop the column entirely (e.g. no permission). */
  hidden?: boolean;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: string;
  className?: string;
  headerClassName?: string;
  /** Where this column goes in the mobile card. Defaults to `meta`. */
  mobile?: DataTableMobileSlot;
  /** Label shown before the value for `meta` items (defaults to `header` if a string). */
  mobileLabel?: React.ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  size?: 'sm';
  striped?: boolean;
  bordered?: boolean;
  hover?: boolean;
  /** Wrap the desktop table in a white rounded panel (Material card). */
  framed?: boolean;
  /** Sticky header on the desktop table (needs a scroll-height ancestor). */
  stickyHeader?: boolean;
  /** Controlled sort state (server or client); enables sortable header UI. */
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
  /**
   * Footer row content (e.g. a totals row), as `<tr><td>…` markup.
   *
   * Rendered in the desktop `<tfoot>` AND, in its own card, at the end of the mobile list. It
   * used to be desktop-only, which meant a totals row simply vanished on a phone — the reader
   * got a list of figures and no total, with nothing to indicate one existed. Since callers
   * pass table-row markup, the mobile copy wraps it in a minimal table so the `<tr>`/`<td>`
   * stays valid; a `colSpan` sized for the desktop column count degrades harmlessly there.
   */
  footer?: React.ReactNode;
  /**
   * Expanded detail for a row. Supply both this and `isExpanded` to make rows expandable: on
   * desktop the content renders in a full-width row underneath, on mobile inside the card.
   *
   * Added so an expandable list does not have to abandon DataTable and hand-roll a table
   * (which is what VendorsPage had to do), losing sorting, the mobile card layout and the
   * empty/loading states with it. Both props are optional, so every existing caller is
   * unaffected.
   */
  renderExpanded?: (row: T) => React.ReactNode;
  isExpanded?: (row: T) => boolean;
  className?: string;
}

const alignClass = (a?: 'left' | 'right' | 'center') =>
  a === 'right' ? 'tw-text-right' : a === 'center' ? 'tw-text-center' : '';

function SortIcon({ active, dir }: { active: boolean; dir?: 'asc' | 'desc' }) {
  if (!active) return <i className="bi bi-arrow-down-up tw-ml-1 tw-text-content-muted" />;
  return <i className={`bi ${dir === 'asc' ? 'bi-sort-down-alt' : 'bi-sort-down'} tw-ml-1`} />;
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  renderExpanded,
  isExpanded,
  loading = false,
  emptyMessage = 'No records found',
  size,
  striped,
  bordered,
  hover = true,
  framed = false,
  stickyHeader = false,
  sortBy,
  sortDir,
  onSort,
  footer,
  className = '',
}: DataTableProps<T>) {
  const cols = columns.filter((c) => !c.hidden);
  const mobileCols = cols.filter((c) => (c.mobile ?? 'meta') !== 'hidden');
  const titleCol = mobileCols.find((c) => c.mobile === 'title');
  const subtitleCols = mobileCols.filter((c) => c.mobile === 'subtitle');
  const trailingCols = mobileCols.filter((c) => c.mobile === 'trailing');
  const metaCols = mobileCols.filter((c) => (c.mobile ?? 'meta') === 'meta');

  const handleHeaderClick = (c: DataTableColumn<T>) => {
    if (!c.sortable || !onSort) return;
    const nextDir = sortBy === c.key && sortDir === 'asc' ? 'desc' : 'asc';
    onSort(c.key, nextDir);
  };

  const isEmpty = !loading && data.length === 0;

  return (
    <div className={className}>
      {/* Desktop (md+) */}
      <div
        className={`tw-hidden md:tw-block ${
          framed
            ? 'tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-shadow-sm'
            : ''
        }`}
      >
        <div className="tw-w-full tw-overflow-x-auto">
          <Table size={size} striped={striped} bordered={bordered} hover={hover}>
            <thead className={stickyHeader ? 'table-sticky-header' : undefined}>
              <tr>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    className={`${alignClass(c.align)} ${c.sortable && onSort ? 'tw-cursor-pointer tw-select-none' : ''} ${c.headerClassName ?? ''}`}
                    style={c.width ? { width: c.width } : undefined}
                    // Headers ellipsize on a single line; the title keeps a long label readable.
                    title={headerTitle(c.header)}
                    onClick={() => handleHeaderClick(c)}
                  >
                    {c.header}
                    {c.sortable && onSort && (
                      <SortIcon active={sortBy === c.key} dir={sortBy === c.key ? sortDir : undefined} />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={cols.length} className="tw-py-6 tw-text-center tw-text-content-muted">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data.map((row) => {
                  const expanded = !!renderExpanded && !!isExpanded?.(row);
                  return (
                    <React.Fragment key={rowKey(row)}>
                      <tr
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        className={onRowClick ? 'tw-cursor-pointer' : undefined}
                      >
                        {cols.map((c) => (
                          <td key={c.key} className={`${alignClass(c.align)} ${c.className ?? ''}`}>
                            {c.cell(row)}
                          </td>
                        ))}
                      </tr>
                      {expanded && (
                        // Not clickable: the parent row navigates, and a click inside expanded
                        // detail should never navigate away from what you just opened.
                        <tr className="tw-bg-surface-alt">
                          <td colSpan={cols.length} className="tw-p-0">
                            {renderExpanded!(row)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {footer && !isEmpty && <tfoot className="tw-bg-surface-alt tw-font-semibold">{footer}</tfoot>}
          </Table>
        </div>
      </div>

      {/* Mobile (< md): stacked cards from the same column config */}
      <div className="tw-flex tw-flex-col tw-gap-2 md:tw-hidden">
        {loading && data.length === 0 ? (
          <div className="tw-flex tw-justify-center tw-py-6">
            <Spinner className="tw-text-primary" />
          </div>
        ) : isEmpty ? (
          <div className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-6 tw-text-center tw-text-content-muted">
            {emptyMessage}
          </div>
        ) : (
          data.map((row) => (
            <div
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm ${
                onRowClick ? 'tw-cursor-pointer' : ''
              }`}
            >
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <div className="tw-min-w-0">
                  {titleCol && (
                    <div className="tw-break-words tw-font-semibold tw-text-content">{titleCol.cell(row)}</div>
                  )}
                  {subtitleCols.map((c) => (
                    <div key={c.key} className="tw-mt-0.5 tw-break-words tw-text-sm tw-text-content-secondary">
                      {c.cell(row)}
                    </div>
                  ))}
                </div>
                {trailingCols.length > 0 && (
                  <div
                    className="tw-flex tw-shrink-0 tw-items-center tw-gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {trailingCols.map((c) => (
                      <React.Fragment key={c.key}>{c.cell(row)}</React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              {metaCols.length > 0 && (
                <div className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-sm">
                  {metaCols.map((c) => (
                    <div key={c.key} className="tw-min-w-0">
                      <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-content-muted">
                        {c.mobileLabel ?? c.header}
                      </div>
                      <div className="tw-break-words tw-text-content">{c.cell(row)}</div>
                    </div>
                  ))}
                </div>
              )}
              {renderExpanded && isExpanded?.(row) && (
                <div
                  className="tw--mx-3 tw--mb-3 tw-mt-3 tw-border-t tw-border-line tw-bg-surface-alt"
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderExpanded(row)}
                </div>
              )}
            </div>
          ))
        )}
        {footer && !isEmpty && (
          // The same totals row the desktop <tfoot> gets, so a phone reader is not left with
          // figures and no total.
          <div className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface-alt tw-px-3 tw-py-2 tw-font-semibold tw-text-content">
            <table className="tw-w-full tw-text-sm tw-tabular-nums">
              <tbody>{footer}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataTable;
